import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, BookOpen, Lock, Plus, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATE_LABEL = {
  pending: "Awaiting an organizer",
  awaiting_owner: "Awaiting the owner's permission",
  declined: "Declined",
};

/** Associate one of MY events/resources, or start a new one in the CANONICAL editor. */
function AddDialog({ group, kind, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [mode, setMode] = React.useState("existing");
  const [pick, setPick] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const plural = kind === "event" ? "events" : "resources";
  const { data: mine } = useQuery({
    queryKey: ["my-content", plural],
    queryFn: async () => (await api.get(`/${plural}/mine`)).data,
    enabled: open,
  });

  React.useEffect(() => {
    if (open) { setMode("existing"); setPick(""); }
  }, [open]);

  const submit = async () => {
    // GRP-DEC-315 — creating goes to the ONE canonical Event/Resource editor, carrying the
    // Group as context. There is no reduced Group-specific editor any more.
    if (mode === "new") {
      onOpenChange(false);
      navigate(`/dashboard/${plural}?new=1&group=${group.id}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/content`,
        { content_type: kind, content_id: pick });
      toast.success(data.state === "approved"
        ? "Added to the group."
        : "Sent for approval.");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`group-content-add-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>Add {kind === "event" ? "an event" : "a resource"}</DialogTitle>
          <DialogDescription>
            The {kind} keeps its own owner — the group only links to it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid={`group-content-mode-${kind}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="existing">Add one of mine</SelectItem>
              <SelectItem value="new">Create a new one</SelectItem>
            </SelectContent>
          </Select>
          {mode === "existing" ? (
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid={`group-content-pick-${kind}`}>
                <SelectValue placeholder={`Choose one of your ${plural}`} />
              </SelectTrigger>
              <SelectContent>
                {(mine || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}
                              data-testid={`group-content-option-${c.id}`}>
                    {c.title || "Untitled"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
              Opens the full {kind} editor with <strong>{group.display_name}</strong>{" "}
              already selected. You choose there whether it stays members-only or appears
              everywhere it normally would.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button disabled={busy || (mode === "existing" && !pick)}
                  onClick={submit} data-testid={`group-content-submit-${kind}`}>
            {mode === "new" ? `Open ${kind} editor` : `Add ${kind}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Groups Phase 6 — the Group's Events / Resources tab. It shows the ASSOCIATIONS; the
 * objects themselves are always owned and edited by their creator, on their own pages.
 */
export function GroupContentPanel({ group, kind, pendingOnly = false }) {
  const qc = useQueryClient();
  const [adding, setAdding] = React.useState(false);
  const { data } = useQuery({
    queryKey: ["group-content", group.id, kind],
    queryFn: async () => (await api.get(`/groups/${group.id}/content`,
      { params: { content_type: kind } })).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["group-content", group.id, kind] });
  // `pendingOnly` is the Group tab (GRP-DEC-317): the approved items are rendered by the
  // scoped canonical directory underneath, so this strip is only the association workflow
  // — what is awaiting a decision, plus Add. The data and the rules are unchanged.
  const all = data?.items || [];
  const items = pendingOnly ? all.filter((i) => i.state !== "approved") : all;
  const Icon = kind === "event" ? CalendarDays : BookOpen;
  const base = kind === "event" ? "/event" : "/resource";

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "That didn't work."); }
  };

  return (
    <div className="space-y-3" data-testid={`group-content-panel-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">
            {pendingOnly
              ? (kind === "event" ? "Event requests" : "Resource requests")
              : (kind === "event" ? "Events" : "Resources")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {data?.content_approval
              ? "An organizer approves what members add."
              : "Anything members add appears straight away."}
          </p>
        </div>
        {data?.can_associate ? (
          <Button size="sm" onClick={() => setAdding(true)}
                  data-testid={`group-content-add-${kind}`}>
            <Plus className="mr-1.5 h-4 w-4" />Add {kind}
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        pendingOnly ? null : (
          <Card className="border-0 p-8 text-center" data-testid={`group-content-empty-${kind}`}>
            <p className="text-sm text-muted-foreground">
              Nothing here yet.
            </p>
          </Card>
        )
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((i) => (
            <Card key={i.id} className="space-y-2 border-0 p-4"
                  data-testid={`group-content-item-${i.content_id}`}>
              <div className="flex items-start gap-2">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-[600]">
                    {i.content.slug && i.content.published && !i.group_only ? (
                      <a href={`${base}/${i.content.slug}`} className="hover:underline">
                        {i.content.title || "Untitled"}
                      </a>
                    ) : (i.content.title || "Untitled")}
                  </p>
                  {i.content.summary ? (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{i.content.summary}</p>
                  ) : null}
                </div>
                {i.group_only ? (
                  <span className="flex shrink-0 items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                        data-testid={`group-content-members-only-badge-${i.content_id}`}>
                    <Lock className="h-2.5 w-2.5" />members
                  </span>
                ) : null}
              </div>
              {i.state !== "approved" ? (
                <p className="text-xs text-amber-700" data-testid={`group-content-state-${i.content_id}`}>
                  {STATE_LABEL[i.state] || i.state}
                  {i.decline_reason ? ` — ${i.decline_reason}` : ""}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {(i.awaiting_my_consent || (i.state === "pending" && data?.can_manage)) ? (
                  <>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                            data-testid={`group-content-approve-${i.content_id}`}
                            onClick={() => act(() => api.post(
                              `/groups/${group.id}/content/${i.id}/decide`,
                              { approve: true }), "Approved.")}>
                      <Check className="mr-1 h-3.5 w-3.5" />Approve
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            data-testid={`group-content-decline-${i.content_id}`}
                            onClick={() => act(() => api.post(
                              `/groups/${group.id}/content/${i.id}/decide`,
                              { approve: false }), "Declined.")}>
                      <X className="mr-1 h-3.5 w-3.5" />Decline
                    </Button>
                  </>
                ) : null}
                {i.can_remove ? (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                          data-testid={`group-content-remove-${i.content_id}`}
                          onClick={() => act(() => api.delete(
                            `/groups/${group.id}/content/${i.id}`),
                          `Removed from the group — the ${kind} itself is untouched.`)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddDialog group={group} kind={kind} open={adding} onOpenChange={setAdding}
                 onDone={refresh} />
    </div>
  );
}
